import { Form, Input, Button, message, Result } from 'antd';
import { LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '@tradermind/shared/api';
import { resetPasswordSchema, type ResetPasswordFormData } from '@tradermind/shared/validators';
import { ROUTES } from '@tradermind/shared/constants';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenError(true);
    }
  }, [token]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error: { error?: { message?: string; code?: string } }) => {
      if (error.error?.code === 'INVALID_TOKEN' || error.error?.code === 'TOKEN_EXPIRED') {
        setTokenError(true);
      } else {
        message.error(error.error?.message || t('reset_password.failed'));
      }
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!token) return;
    resetPasswordMutation.mutate({
      token,
      new_password: data.password,
    });
  };

  if (tokenError) {
    return (
      <Result
        status="error"
        title={t('reset_password.invalid_token_title')}
        subTitle={t('reset_password.invalid_token_desc')}
        extra={
          <Link to={ROUTES.AUTH.FORGOT_PASSWORD}>
            <Button type="primary">{t('reset_password.reapply')}</Button>
          </Link>
        }
      />
    );
  }

  if (submitted) {
    return (
      <Result
        status="success"
        title={t('reset_password.success_title')}
        subTitle={t('reset_password.success_desc')}
        extra={
          <Link to={ROUTES.AUTH.LOGIN}>
            <Button type="primary">{t('reset_password.login_now')}</Button>
          </Link>
        }
      />
    );
  }

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Link to={ROUTES.AUTH.LOGIN} style={{ color: '#6B7280' }}>
          <ArrowLeftOutlined /> {t('reset_password.back_to_login')}
        </Link>
      </div>

      <h2 style={{ marginBottom: 8 }}>{t('reset_password.title')}</h2>
      <p style={{ color: '#6B7280', marginBottom: 24 }}>
        {t('reset_password.description')}
      </p>

      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Form.Item
          label={t('reset_password.new_password')}
          validateStatus={errors.password ? 'error' : ''}
          help={errors.password?.message}
        >
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input.Password
                {...field}
                prefix={<LockOutlined />}
                placeholder={t('reset_password.new_password_placeholder')}
                size="large"
              />
            )}
          />
        </Form.Item>

        <Form.Item
          label={t('reset_password.confirm_password')}
          validateStatus={errors.confirmPassword ? 'error' : ''}
          help={errors.confirmPassword?.message}
        >
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <Input.Password
                {...field}
                prefix={<LockOutlined />}
                placeholder={t('reset_password.confirm_password_placeholder')}
                size="large"
              />
            )}
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={resetPasswordMutation.isPending}
        >
          {t('reset_password.submit')}
        </Button>
      </Form>
    </>
  );
}
